/**
 * §14.3 / F-019 — a QR code for the join URL.
 *
 * Implemented here rather than pulled from a library because §22.2 caps the
 * player bundle and forbids a large UI dependency on that route, and because
 * §22.1 wants Local Event Mode to need no external request during gameplay.
 *
 * Byte mode, error-correction level M, smallest version that fits the URL.
 */

const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);

(() => {
  let x = 1;
  for (let i = 0; i < 255; i += 1) {
    GF_EXP[i] = x;
    GF_LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i += 1) GF_EXP[i] = GF_EXP[i - 255]!;
})();

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return GF_EXP[GF_LOG[a]! + GF_LOG[b]!]!;
}

function generatorPoly(degree: number): number[] {
  let poly = [1];
  for (let i = 0; i < degree; i += 1) {
    const next = new Array<number>(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j += 1) {
      next[j] = (next[j] ?? 0) ^ gfMul(poly[j]!, 1);
      next[j + 1] = (next[j + 1] ?? 0) ^ gfMul(poly[j]!, GF_EXP[i]!);
    }
    poly = next;
  }
  return poly;
}

function reedSolomon(data: number[], eccLength: number): number[] {
  const generator = generatorPoly(eccLength);
  const result = new Array<number>(eccLength).fill(0);

  for (const byte of data) {
    const factor = byte ^ result[0]!;
    result.shift();
    result.push(0);
    if (factor !== 0) {
      for (let i = 0; i < eccLength; i += 1) {
        result[i] = result[i]! ^ gfMul(generator[i + 1]!, factor);
      }
    }
  }
  return result;
}

/** Total data codewords and ECC-per-block for level M, versions 1–10. */
const VERSION_M: Array<{
  version: number;
  totalCodewords: number;
  eccPerBlock: number;
  blocks: number[];
}> = [
  { version: 1, totalCodewords: 26, eccPerBlock: 10, blocks: [16] },
  { version: 2, totalCodewords: 44, eccPerBlock: 16, blocks: [28] },
  { version: 3, totalCodewords: 70, eccPerBlock: 26, blocks: [44] },
  { version: 4, totalCodewords: 100, eccPerBlock: 18, blocks: [32, 32] },
  { version: 5, totalCodewords: 134, eccPerBlock: 24, blocks: [43, 43] },
  { version: 6, totalCodewords: 172, eccPerBlock: 16, blocks: [27, 27, 27, 27] },
  { version: 7, totalCodewords: 196, eccPerBlock: 18, blocks: [31, 31, 31, 31] },
  { version: 8, totalCodewords: 242, eccPerBlock: 22, blocks: [38, 38, 38, 38] },
  { version: 9, totalCodewords: 292, eccPerBlock: 22, blocks: [36, 36, 36, 36, 37] },
  { version: 10, totalCodewords: 346, eccPerBlock: 26, blocks: [43, 43, 43, 43, 44] },
];

const ALIGNMENT_CENTERS: Record<number, number[]> = {
  1: [],
  2: [6, 18],
  3: [6, 22],
  4: [6, 26],
  5: [6, 30],
  6: [6, 34],
  7: [6, 22, 38],
  8: [6, 24, 42],
  9: [6, 26, 46],
  10: [6, 28, 50],
};

const FORMAT_M = [0x5412, 0x5125, 0x5e7c, 0x5b4b, 0x45f9, 0x40ce, 0x4f97, 0x4aa0];

export interface QrMatrix {
  size: number;
  /** Row-major; true is a dark module. */
  modules: boolean[][];
}

export function encodeQr(text: string): QrMatrix {
  const bytes = [...new TextEncoder().encode(text)];

  const spec = VERSION_M.find((candidate) => {
    const dataCodewords = candidate.blocks.reduce((total, count) => total + count, 0);
    // 4 mode bits + 8 or 16 length bits + payload.
    const lengthBits = candidate.version < 10 ? 8 : 16;
    return dataCodewords * 8 >= 4 + lengthBits + bytes.length * 8;
  });
  if (!spec) throw new Error("QR payload too long for version 10 at level M");

  // ── Bit stream ───────────────────────────────────────────────────────────
  const bits: number[] = [];
  const push = (value: number, length: number) => {
    for (let i = length - 1; i >= 0; i -= 1) bits.push((value >> i) & 1);
  };

  push(0b0100, 4); // byte mode
  push(bytes.length, spec.version < 10 ? 8 : 16);
  for (const byte of bytes) push(byte, 8);

  const dataCodewords = spec.blocks.reduce((total, count) => total + count, 0);
  const capacityBits = dataCodewords * 8;
  push(0, Math.min(4, capacityBits - bits.length)); // terminator
  while (bits.length % 8 !== 0) bits.push(0);

  const codewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j += 1) byte = (byte << 1) | bits[i + j]!;
    codewords.push(byte);
  }
  const padBytes = [0xec, 0x11];
  let padIndex = 0;
  while (codewords.length < dataCodewords) {
    codewords.push(padBytes[padIndex % 2]!);
    padIndex += 1;
  }

  // ── Interleaved blocks with error correction ─────────────────────────────
  const dataBlocks: number[][] = [];
  const eccBlocks: number[][] = [];
  let offset = 0;
  for (const blockSize of spec.blocks) {
    const block = codewords.slice(offset, offset + blockSize);
    offset += blockSize;
    dataBlocks.push(block);
    eccBlocks.push(reedSolomon(block, spec.eccPerBlock));
  }

  const finalCodewords: number[] = [];
  const maxDataLength = Math.max(...spec.blocks);
  for (let i = 0; i < maxDataLength; i += 1) {
    for (const block of dataBlocks) if (i < block.length) finalCodewords.push(block[i]!);
  }
  for (let i = 0; i < spec.eccPerBlock; i += 1) {
    for (const block of eccBlocks) finalCodewords.push(block[i]!);
  }

  // ── Matrix ───────────────────────────────────────────────────────────────
  const size = 17 + spec.version * 4;
  const modules: (boolean | null)[][] = Array.from({ length: size }, () =>
    new Array<boolean | null>(size).fill(null),
  );

  const placeFinder = (row: number, col: number) => {
    for (let r = -1; r <= 7; r += 1) {
      for (let c = -1; c <= 7; c += 1) {
        const rr = row + r;
        const cc = col + c;
        if (rr < 0 || rr >= size || cc < 0 || cc >= size) continue;
        const inRing =
          (r >= 0 && r <= 6 && (c === 0 || c === 6)) || (c >= 0 && c <= 6 && (r === 0 || r === 6));
        const inCore = r >= 2 && r <= 4 && c >= 2 && c <= 4;
        modules[rr]![cc] = inRing || inCore;
      }
    }
  };

  placeFinder(0, 0);
  placeFinder(0, size - 7);
  placeFinder(size - 7, 0);

  // Timing patterns.
  for (let i = 8; i < size - 8; i += 1) {
    modules[6]![i] = i % 2 === 0;
    modules[i]![6] = i % 2 === 0;
  }

  // Alignment patterns.
  const centers = ALIGNMENT_CENTERS[spec.version] ?? [];
  for (const row of centers) {
    for (const col of centers) {
      if (
        (row === 6 && col === 6) ||
        (row === 6 && col === size - 7) ||
        (row === size - 7 && col === 6)
      )
        continue;
      for (let r = -2; r <= 2; r += 1) {
        for (let c = -2; c <= 2; c += 1) {
          modules[row + r]![col + c] = Math.max(Math.abs(r), Math.abs(c)) !== 1;
        }
      }
    }
  }

  // Dark module and reserved format areas.
  modules[size - 8]![8] = true;
  for (let i = 0; i < 9; i += 1) {
    if (modules[8]![i] === null) modules[8]![i] = false;
    if (modules[i]![8] === null) modules[i]![8] = false;
  }
  for (let i = size - 8; i < size; i += 1) {
    if (modules[8]![i] === null) modules[8]![i] = false;
    if (modules[i]![8] === null) modules[i]![8] = false;
  }

  // ── Data placement, mask 0 ───────────────────────────────────────────────
  let bitIndex = 0;
  const totalBits = finalCodewords.length * 8;
  let upward = true;

  for (let col = size - 1; col > 0; col -= 2) {
    if (col === 6) col -= 1; // skip the vertical timing column
    for (let step = 0; step < size; step += 1) {
      const row = upward ? size - 1 - step : step;
      for (const offsetCol of [col, col - 1]) {
        if (modules[row]![offsetCol] !== null) continue;
        let dark = false;
        if (bitIndex < totalBits) {
          const byte = finalCodewords[bitIndex >> 3]!;
          dark = ((byte >> (7 - (bitIndex & 7))) & 1) === 1;
          bitIndex += 1;
        }
        // Mask pattern 0: (row + col) % 2 === 0.
        if ((row + offsetCol) % 2 === 0) dark = !dark;
        modules[row]![offsetCol] = dark;
      }
    }
    upward = !upward;
  }

  // Format information for level M, mask 0.
  const format = FORMAT_M[0]!;
  for (let i = 0; i < 15; i += 1) {
    const bit = ((format >> i) & 1) === 1;
    if (i < 6) modules[i]![8] = bit;
    else if (i < 8) modules[i + 1]![8] = bit;
    else if (i === 8) modules[8]![7] = bit;
    else modules[8]![14 - i] = bit;

    if (i < 8) modules[8]![size - 1 - i] = bit;
    else modules[size - 15 + i]![8] = bit;
  }

  return {
    size,
    modules: modules.map((row) => row.map((cell) => cell === true)),
  };
}

/** Renders the matrix as a standalone SVG string, quiet zone included. */
export function qrSvg(text: string, options: { size?: number; quietZone?: number } = {}): string {
  const { size: pixelSize = 240, quietZone = 4 } = options;
  const matrix = encodeQr(text);
  const total = matrix.size + quietZone * 2;

  let path = "";
  for (let row = 0; row < matrix.size; row += 1) {
    for (let col = 0; col < matrix.size; col += 1) {
      if (matrix.modules[row]![col]) {
        path += `M${col + quietZone} ${row + quietZone}h1v1h-1z`;
      }
    }
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${pixelSize}" height="${pixelSize}" viewBox="0 0 ${total} ${total}" shape-rendering="crispEdges" role="img" aria-label="Join QR code"><rect width="${total}" height="${total}" fill="#ffffff"/><path d="${path}" fill="#000000"/></svg>`;
}
